/*
 * Fragrance quiz — sections/fragrance-quiz.liquid
 *
 * Scent profiles come from the Perfume blocks (built-in list in
 * snippets/fragrance-quiz-data.liquid only when there are none). Everything
 * that changes day to day (price, stock, image, title) is fetched live from
 * /products/<handle>.js for a shortlist only, so a sold-out size is never
 * recommended and the profiles never go stale on price.
 *
 * Scoring: see W below. Family +3 (second family +1.5), +2 per matching note,
 * +1 occasion, +1 intensity fit, -5 for an "avoid" hit, budget +2 / -3.
 */
(() => {
  if (customElements.get('fragrance-quiz')) return;

  const W = { family: 3, secondary: 1.5, note: 2, occasion: 1, intensity: 1, avoid: 5, budgetFit: 2, budgetMiss: 3, sampler: 1.5 };
  const SHORTLIST = 8;
  const STORAGE_KEY = 'fragrance-quiz:answers';
  // The result lives in the address (?quiz=who:me,family:AM,notes:VAN+SPICE,…), so going back
  // from a recommended product — or sharing the link — shows the same result again.
  const URL_PARAM = 'quiz';
  const ANSWER_KEYS = ['who', 'style', 'family', 'notes', 'avoid', 'occ', 'budget'];
  const FAMILY_SLUG = { FR: 'fresh', FL: 'floral', FT: 'fruity', GO: 'gourmand', AM: 'amber', WD: 'woody' };
  const SET_ORDER = { mini: 0, trio: 1, duo: 2, discovery: 3, ritual: 4 };

  // Metafield choice labels (Admin → Settings → Custom data) → internal codes.
  // Matching is case-insensitive; the codes themselves are accepted too.
  const CHOICES = {
    family: {
      fresh: 'FR', 'fresh & citrus': 'FR', floral: 'FL', fruity: 'FT', 'fruity & tropical': 'FT',
      sweet: 'GO', gourmand: 'GO', 'sweet & gourmand': 'GO', warm: 'AM', amber: 'AM', 'warm spicy & amber': 'AM',
      woody: 'WD', 'woody, smoky & oud': 'WD',
    },
    notes: {
      citrus: 'CIT', 'citrus & bergamot': 'CIT', sea: 'SEA', 'sea & salt': 'SEA', herbs: 'HERB', 'lavender & herbs': 'HERB',
      flowers: 'FLOR', 'jasmine, rose & white flowers': 'FLOR', tropical: 'TROP', 'tropical fruit & coconut': 'TROP',
      berries: 'BERRY', 'apple, pear & berries': 'BERRY', vanilla: 'VAN', caramel: 'CARA', 'caramel, praline & tonka': 'CARA',
      honey: 'HONEY', 'honey & tobacco': 'HONEY', spices: 'SPICE', 'ginger, cardamom & cinnamon': 'SPICE',
      woods: 'WOOD', 'sandalwood, cedar & vetiver': 'WOOD', oud: 'OUD', 'oud, incense & leather': 'OUD', musk: 'MUSK', 'musk & amber': 'MUSK',
    },
    gender: { feminine: 'F', female: 'F', women: 'F', masculine: 'M', male: 'M', men: 'M', unisex: 'U' },
    intensity: { light: 1, moderate: 2, intense: 3 },
    occasions: { 'every day': 'D', everyday: 'D', 'date night': 'N', 'big events': 'E', events: 'E', summer: 'S' },
    setType: { mini: 'mini', 'travel size': 'mini', trio: 'trio', duo: 'duo', discovery: 'discovery', 'body ritual': 'ritual', ritual: 'ritual' },
  };
  const CODES = new Set(['FR', 'FL', 'FT', 'GO', 'AM', 'WD', 'CIT', 'SEA', 'HERB', 'FLOR', 'TROP', 'BERRY', 'VAN', 'CARA', 'HONEY', 'SPICE', 'WOOD', 'OUD', 'MUSK', 'F', 'M', 'U', 'D', 'N', 'E', 'S']);
  const pick = (map, value) => {
    if (value == null || value === '') return null;
    const raw = String(value).trim();
    if (CODES.has(raw.toUpperCase())) return raw.toUpperCase();
    const hit = map[raw.toLowerCase()];
    return hit === undefined ? (/^\d+$/.test(raw) ? Number(raw) : null) : hit;
  };
  const pickList = (map, values) => (Array.isArray(values) ? values : values ? [values] : []).map((v) => pick(map, v)).filter(Boolean);
  const format = (template, values) => String(template || '').replace(/\{(\w+)\}/g, (m, k) => (k in values ? values[k] : m));

  const escapeHtml = (s) =>
    String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  const snapMl = (ml) => [10, 30, 50, 90, 100, 200].reduce((a, b) => (Math.abs(b - ml) < Math.abs(a - ml) ? b : a));

  function sizeMl(variantTitle, productTitle) {
    const t = variantTitle === 'Default Title' ? productTitle : variantTitle;
    let m = t.match(/(\d+(?:\.\d+)?)\s*ml/i);
    if (m) return snapMl(parseFloat(m[1]));
    m = t.match(/(\d*\.?\d+)\s*(?:fl\.?\s*)?oz/i) || (variantTitle !== 'Default Title' && t.match(/^\s*(\d*\.?\d+)\s*$/));
    if (m) return snapMl(parseFloat(m[1]) * 29.57);
    return 100;
  }

  function money(cents) {
    const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format(cents / 100);
    } catch (e) {
      return `$${(cents / 100).toFixed(0)}`;
    }
  }

  function sized(url, width) {
    if (!url) return '';
    const abs = url.startsWith('//') ? `https:${url}` : url;
    return `${abs}${abs.includes('?') ? '&' : '?'}width=${width}`;
  }

  // Azmere titles are upper case and carry size, format and gender
  // ('GIORGIO ARMANI "ACQUA DI GIO" 3.4OZ EDT SPR MEN'); the card shows size separately,
  // so keep brand and scent name in title case.
  function shortTitle(title) {
    const clean = title
      .replace(/^GIFT SET\s*-\s*/i, 'Gift Set ')
      .replace(/["“”]|(^|\s)'|'(\s|$)/g, ' ')
      .replace(/\(.*?\)/g, ' ')
      .replace(/\s(\d+\.\d+|\d+\s*(OZ|ML)\b)(\s*(OZ|ML))?.*$/i, ' ')
      .replace(/\b(EDP|EDT|EDC|EAU DE (PARFUM|TOILETTE)|PARFUM SPRAY|SPR|SPRAY|FOR|MEN|WOMEN|WOM|UNISEX|TESTER)\b/gi, ' ')
      .replace(/["']?\d+\s*PCS?["']?|\bSET\s*$/gi, ' ')
      .replace(/\s+/g, ' ')
      .replace(/[\s\-+,]+$/, '')
      .trim();
    if (!clean) return title;
    // brand abbreviations stay in capitals
    const KEEP = new Set(['CD', 'GA', 'YSL', 'MFK', 'D&G', 'V&R', 'RL', 'CK', 'JPG', 'DKNY', 'MCM', 'PDM', 'JLO', 'VIP', 'PM', 'AM', 'CH', 'XS', 'II', 'III']);
    return clean
      .split(' ')
      .map((w) => (KEEP.has(w.toUpperCase()) ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()))
      .join(' ');
  }

  // Managers can pick any card colour, including dark ones: flip the text to
  // white when the background (or a gradient's first colour) is dark.
  function isDark(el) {
    const cs = getComputedStyle(el);
    let rgb = cs.backgroundColor.match(/[\d.]+/g);
    if (!rgb || (rgb[3] !== undefined && Number(rgb[3]) === 0)) {
      const first = cs.backgroundImage.match(/rgba?\([^)]+\)|#[0-9a-f]{3,8}/i);
      if (!first) return false;
      if (first[0].startsWith('#')) {
        let hex = first[0].slice(1);
        if (hex.length === 3) hex = [...hex].map((c) => c + c).join('');
        rgb = [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16));
      } else {
        rgb = first[0].match(/[\d.]+/g);
      }
    }
    const [r, g, b] = rgb.map(Number);
    return (0.299 * r + 0.587 * g + 0.114 * b) / 255 < 0.55;
  }

  function markContrast(root) {
    root.querySelectorAll('.fq__tile--world, .fq__persona').forEach((el) => el.classList.toggle('is-dark', isDark(el)));
  }

  function storage(fn) {
    try {
      return fn(window.sessionStorage);
    } catch (e) {
      return null;
    }
  }

  class FragranceQuiz extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      const data = JSON.parse(this.querySelector('[id^="FragranceQuizData-"]').textContent);
      this.products = data.products;
      this.sets = data.sets;
      this.giftHandles = data.gift;
      this.personas = JSON.parse(this.querySelector('[data-fq-personas]').textContent);
      this.t = JSON.parse(this.querySelector('[data-fq-strings]').textContent);
      // Scent world blocks carry their own result profile and colour.
      this.querySelectorAll('.fq__tile--world').forEach((tile) => {
        const f = tile.querySelector('input').value;
        this.personas[f] = {
          ...this.personas[f],
          title: tile.dataset.profileTitle || (this.personas[f] || {}).title,
          tagline: tile.dataset.profileText || (this.personas[f] || {}).tagline,
          tint: tile.style.getPropertyValue('--fq-tint') || '',
        };
      });
      const budgetStep = this.querySelector('[data-step="budget"]');
      const limits = budgetStep ? budgetStep.dataset : {};
      const low = Number(limits.budgetLow) || 10000;
      const mid = Number(limits.budgetMid) || 16000;
      const lux = Number(limits.budgetLux) || 20000;
      this.budget = {
        try: (v) => v.ml <= 10 && v.price <= low,
        u100: (v) => v.price <= low,
        mid: (v) => v.price > low && v.price <= mid,
        lux: (v) => v.price >= lux,
        set: () => true,
      };
      this.priceCap = { try: low, mid };
      // Azmere has ~700 perfumes in snippets/fragrance-quiz-data.liquid — too many to keep as
      // editor blocks. So Perfume / Gift set blocks don't replace that list, they sit on top of it:
      // a block for a product overrides its built-in profile, "Hide" takes it out of the quiz,
      // and a block for a product that isn't in the file adds it.
      const entries = this.blockEntries();
      const perfumeBlocks = entries.filter((e) => 'family' in e);
      this.mergeCustom(JSON.parse(this.querySelector('[data-fq-custom]').textContent || '[]'));
      this.mergeCustom(entries);
      this.linkTwins(perfumeBlocks);
      this.showMatch = this.dataset.showMatch === 'true';
      this.cache = new Map();

      this.form = this.querySelector('[data-fq-form]');
      // Question order = block order in the theme editor. A question added
      // twice only counts once; the extra copy stays hidden.
      this.steps = [];
      this.form.querySelectorAll('fieldset[data-step]').forEach((el) => {
        if (this.steps.includes(el.dataset.step)) {
          el.removeAttribute('data-step');
          el.dataset.duplicate = 'true';
        } else {
          this.steps.push(el.dataset.step);
        }
      });
      const notesStep = this.form.querySelector('[data-step="notes"]');
      this.notesMax = notesStep ? Number(notesStep.dataset.max) || 3 : 3;
      this.notesHint = notesStep ? notesStep.dataset.hint : '';
      this.notesCounter = notesStep ? notesStep.dataset.counter : '';
      this.live = this.querySelector('[data-fq-live]');
      this.noteLabels = {};
      this.form.querySelectorAll('input[name="notes"]').forEach((i) => {
        this.noteLabels[i.value] = i.nextElementSibling.textContent.trim();
      });
      this.worldLabels = {};
      this.form.querySelectorAll('input[name="family"]').forEach((i) => {
        this.worldLabels[i.value] = i.closest('label').querySelector('.fq__tile-label').textContent.trim();
      });

      this.reset();
      this.bind();
      markContrast(this);
      if (window.Shopify && Shopify.designMode) this.bindEditor();

      // Back from the email form (Shopify reloads the page): show the same result again.
      const fromUrl = this.readUrlAnswers();
      if (this.querySelector('[data-fq-posted], .fq__email-error')) {
        const saved = storage((s) => JSON.parse(s.getItem(STORAGE_KEY) || 'null'));
        if (saved) {
          this.answers = saved;
          this.finish(false);
        }
      } else if (fromUrl) {
        // Back from a recommended product, or an opened link: same answers, same result.
        this.answers = fromUrl;
        this.syncForm();
        this.finish(false);
      } else {
        this.startFromUrl();
      }
    }

    // ?start=who:her,family:FL - the first answers were picked on the home page (quiz teaser): tick
    // them and open the quiz on the first question still to answer; Back walks the answered ones.
    startFromUrl() {
      const raw = new URLSearchParams(window.location.search).get('start');
      if (!raw) return;
      const picked = {};
      raw.split(',').forEach((pair) => {
        const [key, value = ''] = pair.split(':');
        if (!['who', 'style', 'family'].includes(key) || !value) return;
        const input = this.form.querySelector(`input[name="${key}"][value="${CSS.escape(value)}"]`);
        if (!input) return;
        input.checked = true;
        picked[key] = value;
      });
      if (!picked.who) return;
      Object.assign(this.answers, picked);
      const path = this.path();
      const answered = path.filter((step) => step in picked);
      const next = path.find((step) => !(step in picked));
      this.history = answered.slice(0, -1);
      this.current = answered[answered.length - 1];
      this.go(next || 'finish');
    }

    // ---------- answers in the address ----------

    readUrlAnswers() {
      const raw = new URLSearchParams(window.location.search).get(URL_PARAM);
      if (!raw) return null;
      const valid = (name, value) => Boolean(this.form.querySelector(`input[name="${name}"][value="${CSS.escape(value)}"]`));
      const answers = { notes: [] };
      raw.split(',').forEach((pair) => {
        const [key, value = ''] = pair.split(':');
        if (!ANSWER_KEYS.includes(key) || !value) return;
        if (key === 'notes') answers.notes = value.split('+').filter((n) => valid('notes', n)).slice(0, this.notesMax);
        else if (valid(key, value)) answers[key] = value;
      });
      return answers.who ? answers : null;
    }

    writeUrlAnswers(answers) {
      const url = new URL(window.location.href);
      const value = answers
        ? ANSWER_KEYS.filter((k) => (k === 'notes' ? answers.notes && answers.notes.length : answers[k]))
            .map((k) => `${k}:${k === 'notes' ? answers.notes.join('+') : answers[k]}`)
            .join(',')
        : '';
      if (value) url.searchParams.set(URL_PARAM, value);
      else url.searchParams.delete(URL_PARAM);
      window.history.replaceState(window.history.state, '', url);
    }

    // Tick the restored answers and rebuild the step history, so "Back" from the result walks the questions.
    syncForm() {
      Object.entries(this.answers).forEach(([name, value]) => {
        (Array.isArray(value) ? value : [value]).forEach((v) => {
          const input = this.form.querySelector(`input[name="${name}"][value="${CSS.escape(v)}"]`);
          if (input) input.checked = true;
        });
      });
      this.updateNotes();
      this.history = this.path().filter((step) => step in this.answers || step === 'notes');
    }

    // "Same scent as" on a Perfume block: the two never share a result.
    linkTwins(entries) {
      entries.forEach((e) => {
        if (!e.twin || e.exclude) return;
        const a = this.products.find((p) => p.h === e.h);
        const b = this.products.find((p) => p.h === e.twin);
        if (!a || !b) return;
        const key = b.tw || a.tw || [a.h, b.h].sort().join('|');
        a.tw = key;
        b.tw = key;
      });
    }

    blockEntries() {
      return [...this.querySelectorAll('script[data-fq-entry]')]
        .map((el) => {
          try {
            return JSON.parse(el.textContent);
          } catch (e) {
            return null;
          }
        })
        .filter(Boolean);
    }

    // Products tagged by the manager with the "quiz" metafields win over the
    // defaults shipped in snippets/fragrance-quiz-data.liquid.
    mergeCustom(entries) {
      entries.forEach((e) => {
        if (!e || !e.h) return;
        const drop = (list) => list.filter((x) => x.h !== e.h);
        if (e.exclude) {
          this.products = drop(this.products);
          this.sets = drop(this.sets);
          this.giftHandles = this.giftHandles.filter((h) => h !== e.h);
          return;
        }
        if (e.contains && e.contains.length) {
          const type = pick(CHOICES.setType, e.setType) || 'discovery';
          this.sets = drop(this.sets).concat({ h: e.h, n: '', type, contains: e.contains });
          // Blocks say explicitly whether a set is a gift idea; metafields go by type.
          const gift = e.gift !== undefined ? e.gift : type === 'discovery' || type === 'trio';
          this.giftHandles = this.giftHandles.filter((h) => h !== e.h);
          if (gift) this.giftHandles.push(e.h);
        }
        const f = pick(CHOICES.family, e.family);
        if (!f) return;
        const previous = this.products.find((p) => p.h === e.h) || {};
        this.products = drop(this.products).concat({
          ...previous,
          h: e.h,
          c: e.c || previous.c || 'Signature',
          f,
          s: pick(CHOICES.family, e.secondary) || '',
          t: pickList(CHOICES.notes, e.notes),
          g: pick(CHOICES.gender, e.gender) || 'U',
          i: pick(CHOICES.intensity, e.intensity) || 2,
          o: e.occ ? Object.keys(e.occ).filter((k) => e.occ[k]) : pickList(CHOICES.occasions, e.occasions),
          b: Number(e.boost) || 0,
          new: e.new !== undefined ? Boolean(e.new) : previous.new,
          q: 1,
        });
      });
    }

    reset() {
      this.answers = { notes: [] };
      this.history = [];
      this.form.reset();
      this.updateNotes();
    }

    bind() {
      this.querySelector('[data-fq-start]').addEventListener('click', () => {
        this.track('start', {});
        this.go(this.path()[0] || 'finish');
      });
      this.querySelector('[data-fq-back]').addEventListener('click', () => this.back());
      const next = this.querySelector('[data-fq-next]');
      if (next) {
        next.addEventListener('click', () => {
          this.track('answer', { question: 'notes', value: this.answers.notes, step: this.path().indexOf('notes') + 1 });
          this.go(this.nextOf('notes'));
        });
      }
      this.querySelector('[data-fq-restart]').addEventListener('click', () => {
        this.track('restart', { profile: this.profileKey });
        this.reset();
        this.writeUrlAnswers(null);
        this.show('intro');
      });

      this.form.addEventListener('change', (e) => {
        const input = e.target;
        if (input.name === 'notes') {
          this.answers.notes = [...this.form.querySelectorAll('input[name="notes"]:checked')].map((i) => i.value);
          this.updateNotes();
          return;
        }
        if (input.type !== 'radio') return;
        this.answers[input.name] = input.value;
        // Arrow keys move the selection inside a radio group; advancing on
        // them would skip the question. Keyboard users confirm with Enter.
        if (this.lastKey && this.lastKey.startsWith('Arrow')) return;
        this.advance(input.name);
      });
      this.form.addEventListener('pointerdown', () => {
        this.lastKey = null;
      });
      this.form.addEventListener('keydown', (e) => {
        this.lastKey = e.key;
        const input = e.target;
        if (e.key === 'Enter' && input.type === 'radio') {
          e.preventDefault();
          if (!input.checked) input.checked = true;
          this.answers[input.name] = input.value;
          this.advance(input.name);
        }
      });
      this.form.addEventListener('submit', (e) => e.preventDefault());

      this.querySelector('[data-fq-cards]').addEventListener('click', (e) => {
        const btn = e.target.closest('[data-fq-add]');
        if (btn) this.addToCart(btn);
        const link = e.target.closest('.fq-card a[href*="/products/"]');
        if (link) {
          const cards = [...this.querySelectorAll('[data-fq-cards] .fq-card')];
          this.track('result_click', {
            product: link.getAttribute('href').split('/products/')[1].split('?')[0],
            position: cards.indexOf(link.closest('.fq-card')) + 1,
            profile: this.profileKey,
          });
        }
      });

      const emailForm = this.querySelector('.fq__email-form');
      if (emailForm) {
        emailForm.addEventListener('submit', () => {
          this.track('email_submit', { profile: this.profileKey });
        });
      }
    }

    disconnectedCallback() {
      if (this.editorListeners) {
        this.editorListeners.forEach(([type, fn]) => document.removeEventListener(type, fn));
        this.editorListeners = null;
      }
    }

    // ---------- theme editor ----------
    // Clicking a block in the sidebar shows that screen here, so the manager
    // always sees what they are editing. The choice survives the re-render
    // Shopify does after every setting change.

    bindEditor() {
      const id = this.dataset.sectionId;
      const state = (window.__fragranceQuizEditor = window.__fragranceQuizEditor || {});
      const onBlock = (e) => {
        if (!e.detail || e.detail.sectionId !== id) return;
        state[id] = e.detail.blockId;
        this.previewBlock(e.detail.blockId);
      };
      const onSection = (e) => {
        if (!e.detail || e.detail.sectionId !== id) return;
        state[id] = null;
        this.reset();
        this.show('intro');
        this.editorNote(this.editorSummary());
      };
      this.editorListeners = [
        ['shopify:block:select', onBlock],
        ['shopify:section:select', onSection],
      ];
      this.editorListeners.forEach(([type, fn]) => document.addEventListener(type, fn));
      this.editorNote(this.editorSummary());
      if (state[id]) this.previewBlock(state[id]);
    }

    editorSummary() {
      const fromBlocks = new Set(this.blockEntries().filter((b) => b.family && !b.exclude).map((b) => b.h)).size;
      return `Editor preview · ${this.products.length} perfumes in the quiz${
        fromBlocks ? '' : ' (built-in list: the Perfumes block is empty)'
      } · Click any block on the left to see that screen here. Shoppers never see this bar.`;
    }

    editorNote(text) {
      const bar = this.querySelector('[data-fq-editor]');
      if (bar) bar.textContent = text;
    }

    async previewBlock(blockId) {
      const el = this.querySelector(`[data-block-id="${blockId}"]`);
      if (!el) return;
      // A newer click wins: every await below re-checks this token.
      const token = (this.previewToken = (this.previewToken || 0) + 1);
      const stale = () => token !== this.previewToken;
      this.querySelectorAll('.is-editor-focus').forEach((x) => x.classList.remove('is-editor-focus'));
      this.reset();

      if (el.matches('fieldset[data-step]')) {
        const step = el.dataset.step;
        if (step === 'style') this.answers.who = 'me';
        this.show(step);
        const n = this.steps.indexOf(step) + 1;
        this.editorNote(`Editing question ${n} of ${this.steps.length}. Drag the block to change the order, or remove it to skip this question.`);
        return;
      }
      if (el.classList.contains('fq__tile--world')) {
        this.show('family');
        el.classList.add('is-editor-focus');
        this.editorNote('Editing a scent world card. Its profile name and text appear on the result page when this family wins.');
        return;
      }
      if (el.classList.contains('fq__email')) {
        this.answers = { who: 'me', family: 'FR', notes: ['CIT'], occ: 'D', budget: 'set' };
        await this.finish(false);
        if (stale()) return;
        el.scrollIntoView({ block: 'center' });
        this.editorNote('Editing the email form. It appears under every result.');
        return;
      }

      const type = el.dataset.blockType;
      if (el.dataset.duplicate) {
        this.show('intro');
        this.editorNote('This question is already in the quiz, so this copy is ignored. Remove it.');
        return;
      }
      if (el.classList.contains('fq__chip')) {
        this.show('notes');
        el.classList.add('is-editor-focus');
        this.editorNote('Editing a note chip. The "Note" list decides which perfumes it matches; "Custom chip text" only renames the chip.');
        return;
      }
      if (type === 'perfumes' || type === 'gift_sets') {
        this.show('intro');
        const n = new Set(
          this.blockEntries()
            .filter((b) => (type === 'perfumes' ? b.family !== undefined : b.setType !== undefined))
            .map((b) => b.h)
        ).size;
        this.editorNote(
          type === 'perfumes'
            ? `${this.products.length} perfumes are in the quiz (${n} Perfume blocks). Click a perfume to see who gets it. Add a block for a new perfume; delete or hide a block to take one out.`
            : `${n} gift sets and miniatures are known to the quiz. Add a Gift set block for a new one.`
        );
        return;
      }
      const entry = (() => {
        const script = el.querySelector('script[data-fq-entry]');
        return script ? JSON.parse(script.textContent) : null;
      })();
      if (!entry) {
        this.show('intro');
        this.editorNote(type === 'gift_set' ? 'Choose a gift set product for this block.' : 'Choose a perfume for this block.');
        return;
      }
      const product = await this.fetchProduct(entry.h);
      if (stale()) return;
      const name = product ? shortTitle(product.title) : entry.h;

      if (type === 'gift_set') {
        this.answers = { who: 'unsure', occ: 'E', budget: 'set' };
        await this.finish(false);
        if (stale()) return;
        const shown = [...this.querySelectorAll('.fq-card__title a')].some((a) => a.getAttribute('href').includes(`/products/${entry.h}?`));
        let why = shown ? `${name} is suggested here.` : `${name} is not in these top 3 gift picks.`;
        if (entry.exclude) why = `${name} is hidden from the quiz.`;
        else if (product && !product.available) why = `${name} is sold out, so the quiz skips it until it is back in stock.`;
        else if (!entry.gift) why += ' Tick "Suggest for not sure of their taste gifts" to include it here.';
        this.editorNote(`Preview: a gift for someone whose taste is unknown. ${why} It also appears as "${this.t.giftSet}" / "${this.t.tryFirst}" next to the perfumes it contains.`);
        return;
      }

      const profile = this.products.find((x) => x.h === entry.h);
      if (entry.exclude || !profile) {
        this.show('intro');
        this.editorNote(`${name} · Hidden from the quiz.`);
        return;
      }
      this.answers = {
        who: 'me',
        style: 'any',
        family: profile.f,
        notes: profile.t.slice(0, this.notesMax),
        avoid: 'none',
        occ: profile.o[0] || 'D',
      };
      await this.finish(false);
      if (stale()) return;
      const picked = [this.worldLabels[profile.f] || (this.personas[profile.f] || {}).label || profile.f, ...this.answers.notes.map((n) => this.noteLabels[n] || n)].join(', ');
      let rank = '';
      if (product && !product.available) rank = 'Sold out, so the quiz skips it until it is back in stock.';
      else {
        const at = (this.lastRanked || []).findIndex((r) => r.p.h === entry.h);
        rank =
          at === 0
            ? 'It is the best match.'
            : at > 0
              ? `It ranks #${at + 1}. Raise "Boost" to move it up.`
              : 'It did not make the shortlist. Check its notes or raise "Boost".';
      }
      this.editorNote(`${name} · Preview for a shopper who picks ${picked}. ${rank}`);
    }

    // ---------- flow ----------

    advance(question) {
      this.track('answer', { question, value: this.answers[question], step: this.path().indexOf(question) + 1 });
      clearTimeout(this.advanceTimer);
      this.advanceTimer = setTimeout(() => this.go(this.nextOf(question)), 220);
    }

    path() {
      const who = this.answers.who;
      if (who === 'unsure') return this.steps.filter((s) => ['who', 'occ', 'budget'].includes(s));
      // Style is only asked of people buying for themselves. Before the first
      // answer, count the longest path so the counter never grows.
      if (who && who !== 'me') return this.steps.filter((s) => s !== 'style');
      return this.steps;
    }

    nextOf(step) {
      const p = this.path();
      const i = p.indexOf(step);
      return i === -1 || i === p.length - 1 ? 'finish' : p[i + 1];
    }

    go(step) {
      if (step === 'finish') return this.finish(true);
      const current = this.current;
      if (current && current !== 'intro') this.history.push(current);
      this.show(step);
    }

    back() {
      const prev = this.history.pop();
      this.show(prev || 'intro');
    }

    show(step) {
      this.current = step;
      const inForm = !['intro', 'loading', 'result'].includes(step);
      this.form.hidden = !inForm;
      this.querySelectorAll('[data-step]').forEach((el) => {
        el.hidden = el.dataset.step !== step;
      });
      const el = this.querySelector(`[data-step="${step}"]`);

      if (inForm) {
        const p = this.path();
        const n = p.indexOf(step) + 1;
        const pct = Math.round(((n - 1) / p.length) * 100);
        this.querySelector('[data-fq-progress]').style.width = `${Math.max(pct, 4)}%`;
        this.querySelector('.fq__progress').setAttribute('aria-valuenow', pct);
        this.querySelector('[data-fq-count]').textContent = `${n} / ${p.length}`;
      }

      const focusTarget = el.querySelector('.fq__q, .fq__persona-title, .fq__result-heading, .fq__title');
      if (focusTarget && step !== 'intro') {
        focusTarget.setAttribute('tabindex', '-1');
        focusTarget.focus({ preventScroll: true });
      }
      const top = this.getBoundingClientRect().top + window.scrollY - 80;
      if (window.scrollY > top) window.scrollTo({ top, behavior: 'smooth' });
    }

    updateNotes() {
      const picked = this.answers.notes.length;
      this.form.querySelectorAll('input[name="notes"]').forEach((i) => {
        i.disabled = !i.checked && picked >= this.notesMax;
      });
      const next = this.querySelector('[data-fq-next]');
      if (next) next.disabled = picked === 0;
      const counter = this.querySelector('[data-fq-notes-count]');
      if (counter) counter.textContent = picked === 0 ? this.notesHint : format(this.notesCounter, { count: picked });
    }

    // ---------- scoring ----------

    eligible(p) {
      const a = this.answers;
      const gender = a.who === 'her' ? 'F' : a.who === 'him' ? 'M' : a.style === 'F' || a.style === 'M' ? a.style : null;
      return !gender || p.g === gender || p.g === 'U';
    }

    violates(p) {
      const avoid = this.answers.avoid;
      const fam = [p.f, p.s];
      if (avoid === 'sweet') return fam.includes('GO') || (p.t.includes('VAN') && p.t.includes('CARA'));
      if (avoid === 'floral') return fam.includes('FL');
      if (avoid === 'heavy') return p.f === 'WD' || p.t.includes('OUD');
      return false;
    }

    baseScore(p) {
      const a = this.answers;
      let s = 0;
      const hits = a.notes.filter((n) => p.t.includes(n));
      if (a.family && a.family !== 'SURPRISE') {
        if (p.f === a.family) s += W.family;
        else if (p.s === a.family) s += W.secondary;
      }
      s += W.note * hits.length;
      if (p.o.includes(a.occ)) s += W.occasion;
      const light = a.occ === 'D' || a.occ === 'S';
      if ((light && p.i <= 2) || (!light && p.i >= 2)) s += W.intensity;
      if (a.occ === 'S' && p.i === 3) s -= 1;
      if (this.violates(p)) s -= W.avoid;
      s += (p.q ? 0.5 : 0) + (p.new ? 0.2 : 0) + (p.b || 0);
      return { score: s, hits };
    }

    maxScore() {
      const a = this.answers;
      const fam = a.family && a.family !== 'SURPRISE' ? W.family : 0;
      const budget = a.budget === 'try' ? W.sampler : a.budget === 'set' ? 0 : W.budgetFit;
      return fam + W.note * a.notes.length + W.occasion + W.intensity + budget + 0.7;
    }

    async fetchProduct(handle) {
      if (!this.cache.has(handle)) {
        const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
        this.cache.set(
          handle,
          fetch(`${root}products/${handle}.js`)
            .then((r) => (r.ok ? r.json() : null))
            .then((p) => {
              if (!p) return null;
              p.variants.forEach((v) => {
                v.ml = sizeMl(v.title, p.title);
              });
              return p;
            })
            .catch(() => null)
        );
      }
      return this.cache.get(handle);
    }

    pickVariant(variants, fit) {
      const budget = this.answers.budget;
      const standard = (list) => list.reduce((a, b) => (Math.abs(b.ml - 100) < Math.abs(a.ml - 100) ? b : a));
      // No budget question in the quiz: show the standard bottle.
      if (!budget) return standard(variants);
      if (fit.length) {
        // Luxury buyers get the standard bottle, not the 200 ml refill size.
        return budget === 'lux'
          ? fit.reduce((a, b) => (Math.abs(b.ml - 100) < Math.abs(a.ml - 100) ? b : a))
          : fit.reduce((a, b) => (b.price < a.price ? b : a));
      }
      // Nothing in budget: a price-led shopper gets the cheapest size in stock,
      // anyone else the size closest to a standard 100 ml bottle.
      if (['try', 'u100', 'mid'].includes(budget)) return variants.reduce((a, b) => (b.price < a.price ? b : a));
      return variants.reduce((a, b) => (Math.abs(b.ml - 100) < Math.abs(a.ml - 100) ? b : a));
    }

    hasMini(handle) {
      return this.sets.some((s) => s.type === 'mini' && s.contains.includes(handle));
    }

    async rank() {
      const a = this.answers;
      const pool = this.products
        .filter((p) => this.eligible(p))
        .map((p) => ({ p, ...this.baseScore(p) }))
        .sort((x, y) => y.score - x.score);

      const ranked = [];
      for (let i = 0; i < pool.length && ranked.length < SHORTLIST; i += SHORTLIST) {
        const batch = pool.slice(i, i + SHORTLIST);
        const live = await Promise.all(batch.map((c) => this.fetchProduct(c.p.h)));
        batch.forEach((c, k) => {
          const product = live[k];
          if (!product || !product.available) return;
          const variants = product.variants.filter((v) => v.available);
          if (!variants.length) return;
          const fit = variants.filter(this.budget[a.budget] || this.budget.set);
          let score = c.score;
          if (a.budget === 'try') {
            if (fit.length || this.hasMini(c.p.h)) score += W.sampler;
          } else if (a.budget !== 'set') {
            score += fit.length ? W.budgetFit : -W.budgetMiss;
          }
          ranked.push({ ...c, score, product, variants, variant: this.pickVariant(variants, fit) });
        });
      }
      return ranked.sort((x, y) => y.score - x.score);
    }

    hand(ranked) {
      if (!ranked.length) return [];
      const hero = ranked[0];
      const picked = [hero];
      const clash = (c) => picked.some((x) => x.p.h === c.p.h || (c.p.tw && c.p.tw === x.p.tw));
      const choose = (rule) => {
        const c = ranked.find((x) => !clash(x) && rule(x));
        if (c) picked.push(c);
        return c;
      };
      const alt1 = choose((x) => x.p.c !== hero.p.c) || choose(() => true);
      if (alt1) choose((x) => x.p.c !== alt1.p.c) || choose(() => true);
      return picked;
    }

    async sampler(item) {
      const own = item.variants.find((v) => v.ml <= 10 && v.price <= this.priceCap.try);
      const sets = this.sets
        .filter((s) => s.contains.includes(item.p.h))
        .sort((x, y) => SET_ORDER[x.type] - SET_ORDER[y.type]);
      for (const s of sets) {
        if (s.type !== 'mini' && own) break;
        const product = await this.fetchProduct(s.h);
        if (product && product.available) {
          const variant = product.variants.find((v) => v.available);
          const cap = this.answers.budget === 'set' ? Infinity : this.answers.budget === 'try' ? this.priceCap.try : this.priceCap.mid;
          if (s.type === 'mini' || variant.price <= cap) {
            return { product, variant, kind: s.type === 'mini' ? this.t.tryFirst : this.t.giftSet, setName: s.n };
          }
        }
      }
      if (own) return { product: item.product, variant: own, kind: this.t.tryFirst };
      return null;
    }

    async giftSets() {
      const live = await Promise.all(this.giftHandles.map((h) => this.fetchProduct(h)));
      const fit = this.budget[this.answers.budget] || this.budget.set;
      const available = live
        .filter((p) => p && p.available)
        .map((p) => ({ product: p, variant: p.variants.find((v) => v.available) }))
        .sort((x, y) => x.variant.price - y.variant.price);
      const inBudget = available.filter((x) => fit({ ...x.variant, ml: 100 }));
      // Azmere sells sets from dozens of brands: cheapest first, but one per brand.
      const picks = [];
      const brands = new Set();
      for (const x of inBudget.length ? inBudget : available) {
        const brand = String(x.product.vendor || '').toLowerCase();
        if (brands.has(brand)) continue;
        brands.add(brand);
        picks.push(x);
        if (picks.length === 3) break;
      }
      return picks;
    }

    // ---------- result ----------

    async finish(animate) {
      const run = (this.finishRun = (this.finishRun || 0) + 1);
      this.show('loading');
      const started = Date.now();
      storage((s) => s.setItem(STORAGE_KEY, JSON.stringify(this.answers)));
      if (!(window.Shopify && Shopify.designMode)) this.writeUrlAnswers(this.answers);

      let cards = [];
      let persona;
      let notice = '';
      if (this.answers.who === 'unsure') {
        const sets = await this.giftSets();
        cards = sets.map((x, i) => ({ ...x, kind: i === 0 ? this.t.giftPick : this.t.alsoGift }));
        persona = { key: 'gift', ...this.personas.gift };
      } else {
        const ranked = await this.rank();
        if (run === this.finishRun) this.lastRanked = ranked;
        const hand = this.hand(ranked);
        if (hand.length) {
          const max = this.maxScore();
          cards = hand.map((c, i) => ({
            product: c.product,
            variant: c.variant,
            kind: i === 0 ? this.t.bestMatch : this.t.alsoForYou,
            hero: i === 0,
            hits: c.hits,
            familyHit: c.p.f === this.answers.family,
            // Boost is a merchandising lever, not a better fit: keep it out of the %.
            match: i === 0 ? Math.min(99, Math.round(((c.score - (c.p.b || 0)) / max) * 100)) : null,
          }));
          let extra = await this.sampler(hand[0]);
          if (!extra && this.answers.budget === 'try') {
            // Most travel sizes sell out; walk down the shortlist (never past
            // a scent that clashes with the answers) to the best one in stock.
            for (const alt of ranked.slice(1)) {
              if (this.violates(alt.p)) continue;
              extra = await this.sampler(alt);
              if (extra) {
                extra.kind = this.t.tryFirst;
                if (!extra.hits) extra.hits = alt.hits;
                break;
              }
            }
          }
          if (!extra && this.answers.budget === 'set') {
            const [gift] = await this.giftSets();
            if (gift) extra = { ...gift, kind: this.t.giftSet };
          }
          // The hero may already be shown in its travel size: don't repeat it.
          if (extra && cards.some((c) => c.variant.id === extra.variant.id)) extra = null;
          if (extra && this.answers.budget === 'set') cards.unshift(extra);
          else if (extra) cards.push(extra);
          const heroIsTravel = cards[0] && cards[0].variant.ml <= 10;
          if (!extra && this.answers.budget === 'try' && !heroIsTravel) {
            notice = this.t.soldOut;
          }
          const key = this.answers.family && this.answers.family !== 'SURPRISE' ? this.answers.family : hand[0].p.f;
          persona = { key, ...this.personas[key] };
        }
      }

      if (animate) {
        const wait = 900 - (Date.now() - started);
        if (wait > 0) await new Promise((r) => setTimeout(r, wait));
      }

      if (run !== this.finishRun) return;
      this.renderResult(persona, cards, notice);
      this.profileKey = persona ? persona.key : null;
      const tags = this.querySelector('[data-fq-tags]');
      if (tags && persona) tags.value = `newsletter,fragrance-quiz,quiz-${FAMILY_SLUG[persona.key] || persona.key}`;
      this.track('complete', {
        profile: this.profileKey,
        products: cards.map((c) => c.product.handle),
        result_count: cards.length,
        answer: this.answers,
      });
    }

    renderResult(persona, cards, notice) {
      const personaEl = this.querySelector('[data-fq-persona]');
      const cardsEl = this.querySelector('[data-fq-cards]');

      if (!persona || !cards.length) {
        personaEl.innerHTML = '';
        cardsEl.innerHTML = `<p class="fq__empty">${escapeHtml(this.t.empty)}
          <a href="${(window.routes && window.routes.collections_url) || '/collections'}/all">${escapeHtml(this.t.browse)}</a></p>`;
        this.show('result');
        return;
      }

      const slug = FAMILY_SLUG[persona.key] || persona.key;
      const chosen = [];
      if (this.answers.family && this.answers.family !== 'SURPRISE') chosen.push(this.worldLabels[this.answers.family]);
      (this.answers.notes || []).forEach((n) => chosen.push(this.noteLabels[n]));
      personaEl.className = `fq__persona fq__persona--${slug}`;
      personaEl.style.setProperty('--fq-tint', persona.tint || '');
      if (!persona.tint) personaEl.style.removeProperty('--fq-tint');
      personaEl.innerHTML = `
        <p class="fq__eyebrow">${escapeHtml(this.t.profileEyebrow)}</p>
        <h2 class="fq__persona-title" tabindex="-1">${escapeHtml(persona.title)}</h2>
        <p class="fq__persona-tagline">${escapeHtml(persona.tagline)}</p>
        ${
          chosen.length && persona.key !== 'gift'
            ? `<ul class="fq__persona-notes" aria-label="${escapeHtml(this.t.profileEyebrow)}">${chosen.map((c) => `<li>${escapeHtml(c)}</li>`).join('')}</ul>`
            : ''
        }`;

      markContrast(this);
      cardsEl.innerHTML =
        (notice ? `<p class="fq__notice">${escapeHtml(notice)}</p>` : '') + cards.map((c) => this.cardHtml(c)).join('');
      this.show('result');
      if (this.live) this.live.textContent = `${this.t.profileEyebrow}: ${persona.title}`;
    }

    cardHtml(c) {
      const { product, variant } = c;
      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      const url = `${root}products/${product.handle}?variant=${variant.id}`;
      const img = (variant.featured_image && variant.featured_image.src) || product.featured_image;
      const size = variant.title === 'Default Title' ? '' : `${escapeHtml(variant.title)} · `;
      const why = [];
      if (c.familyHit) why.push(this.worldLabels[this.answers.family]);
      (c.hits || []).forEach((h) => why.push(this.noteLabels[h]));
      const showMatch = this.showMatch && c.match && c.match >= 60;

      return `
        <article class="fq-card${c.hero ? ' fq-card--hero' : ''}">
          <a class="fq-card__media" href="${url}" tabindex="-1" aria-hidden="true">
            ${img ? `<img src="${escapeHtml(sized(img, 600))}" alt="" loading="lazy" width="600" height="600">` : ''}
          </a>
          <div class="fq-card__body">
            <p class="fq-card__kind">${escapeHtml(c.kind)}${showMatch ? ` <span class="fq-card__match">${escapeHtml(format(this.t.match, { percent: c.match }))}</span>` : ''}</p>
            <h3 class="fq-card__title"><a href="${url}">${escapeHtml(shortTitle(product.title))}</a></h3>
            <p class="fq-card__meta">${size}${money(variant.price)}${
              variant.compare_at_price > variant.price ? ` <s>${money(variant.compare_at_price)}</s>` : ''
            }</p>
            ${
              why.length
                ? `<ul class="fq-card__why">${why.map((w) => `<li>${escapeHtml(w)}</li>`).join('')}</ul>`
                : ''
            }
            <button type="button" class="fq__btn fq__btn--primary fq-card__add" data-fq-add="${variant.id}">${escapeHtml(this.t.addToCart)}</button>
            <p class="fq-card__error" role="alert" hidden></p>
          </div>
        </article>`;
    }

    // ---------- cart ----------

    // Azmere runs Palo Alto: add through /cart/add.js, then let the theme's CartDrawer
    // (window.cart) rebuild and open itself — it fires theme:cart:added like any other add.
    async addToCart(btn) {
      const id = Number(btn.dataset.fqAdd);
      const errorEl = btn.parentElement.querySelector('.fq-card__error');
      const routes = (window.theme && window.theme.routes) || {};
      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');
      errorEl.hidden = true;
      try {
        const res = await fetch(`${routes.cart_add_url || `${root}cart/add`}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items: [{ id, quantity: 1 }] }),
        });
        const json = await res.json();
        if (json.status) throw new Error(json.description || json.message);

        this.track('add_to_cart', { variant: id, profile: this.profileKey });
        if (window.cart && typeof window.cart.getCart === 'function') {
          window.cart.getCart();
        } else {
          window.location = routes.cart_url || `${root}cart`;
        }
      } catch (e) {
        errorEl.textContent = e.message || this.t.addError;
        errorEl.hidden = false;
      } finally {
        btn.disabled = false;
        btn.removeAttribute('aria-busy');
      }
    }

    track(name, detail) {
      if (window.Shopify && Shopify.designMode) return;
      this.dispatchEvent(new CustomEvent(`fragrance-quiz:${name}`, { bubbles: true, detail }));
      try {
        if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
          Shopify.analytics.publish(`fragrance_quiz_${name}`, detail);
        }
      } catch (e) {
        /* analytics must never break the quiz */
      }
      // The site runs gtag.js without GTM, so a {event: ...} push to dataLayer never reaches GA4.
      // GA4 takes flat string/number params of up to 100 chars: arrays are joined, objects spread.
      if (typeof window.gtag === 'function') {
        const params = {};
        const put = (key, value) => {
          if (value == null || value === '') return;
          params[key] = typeof value === 'number' ? value : String(Array.isArray(value) ? value.join(',') : value).slice(0, 100);
        };
        Object.keys(detail).forEach((key) => {
          const value = detail[key];
          if (value && typeof value === 'object' && !Array.isArray(value)) {
            Object.keys(value).forEach((k) => put(`${key}_${k}`, value[k]));
          } else {
            put(key, value);
          }
        });
        window.gtag('event', `quiz_${name}`, params);
      }
    }
  }

  customElements.define('fragrance-quiz', FragranceQuiz);
})();
